/**
 * recipe-saves.js — Local-only "save recipe" hearts.
 *
 * Stores a map of saved recipe handles in localStorage on the user's
 * device. No auth, no backend; intentionally per-device. Wires up:
 *
 *   - <button data-recipe-save data-handle="...">  → toggle save
 *   - data-saved-only-filter buttons               → "Saved (n)" pill
 *   - data-saved-count badges                      → live save count
 *
 * Liquid renders everything in the unsaved state; this script hydrates
 * the correct visual state once on DOMContentLoaded, then again any
 * time storage changes (multi-tab support). Storage shape:
 *
 *   localStorage["sidekick.savedRecipes.v1"] = JSON.stringify({
 *     "recipe-handle": { savedAt: 1730000000000, title: "..." },
 *     ...
 *   })
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'sidekick.savedRecipes.v1';

  function readStore() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return {};
      var parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (e) {
      // Quota errors, private mode, corrupt JSON — fall back to empty.
      return {};
    }
  }

  function writeStore(store) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
    } catch (e) {
      // Storage failed (private mode, quota). Silent — heart still
      // toggles visually for the current page lifetime.
    }
  }

  function isSaved(handle) {
    var store = readStore();
    return Object.prototype.hasOwnProperty.call(store, handle);
  }

  function toggleSave(handle, title) {
    var store = readStore();
    var nowSaved;
    if (Object.prototype.hasOwnProperty.call(store, handle)) {
      delete store[handle];
      nowSaved = false;
    } else {
      store[handle] = {
        savedAt: Date.now(),
        title: title || ''
      };
      nowSaved = true;
    }
    writeStore(store);
    return nowSaved;
  }

  function paintButton(btn) {
    var saved = isSaved(btn.dataset.handle);
    btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
    var label = btn.querySelector('[data-recipe-save-label]');
    if (label && btn.classList.contains('recipe-save--article')) {
      // Update visible label only on the article page (cards keep
      // the visually-hidden "Save recipe" so screen readers always
      // get a stable announcement).
      label.textContent = saved ? 'Saved' : 'Save recipe';
    }
    btn.setAttribute(
      'aria-label',
      (saved ? 'Remove saved recipe: ' : 'Save recipe: ') + (btn.dataset.title || '')
    );
  }

  function paintAllButtons(scope) {
    var root = scope || document;
    var btns = root.querySelectorAll('[data-recipe-save]');
    btns.forEach(paintButton);
  }

  function paintCard(handle) {
    // Mark each listing card with data-saved so the "Saved" filter pill
    // and any future "Saved" badge styling can hook in via attribute
    // selectors. This is idempotent and safe to call repeatedly.
    var saved = isSaved(handle);
    var cards = document.querySelectorAll(
      '.blog-post-item [data-recipe-save][data-handle="' + cssEscape(handle) + '"]'
    );
    cards.forEach(function (btn) {
      var card = btn.closest('.blog-post-item');
      if (!card) return;
      if (saved) {
        card.setAttribute('data-saved', 'true');
      } else {
        card.removeAttribute('data-saved');
      }
    });
  }

  function paintAllCards() {
    var btns = document.querySelectorAll('.blog-post-item [data-recipe-save]');
    btns.forEach(function (btn) {
      paintCard(btn.dataset.handle);
    });
  }

  function updateCounts() {
    var store = readStore();
    var n = Object.keys(store).length;
    var counts = document.querySelectorAll('[data-saved-count]');
    counts.forEach(function (el) {
      el.textContent = String(n);
    });
    // Hide the "Saved" filter pill entirely when there's nothing saved
    // — saves users from a useless empty filter taking up space.
    var pill = document.querySelector('[data-saved-only-filter]');
    if (pill) {
      pill.hidden = n === 0;
    }
  }

  function cssEscape(s) {
    // Tiny CSS.escape polyfill for old Safari. Handles handles, which
    // are kebab-case by Shopify convention — safe enough.
    if (window.CSS && window.CSS.escape) return window.CSS.escape(s);
    return String(s).replace(/[^a-zA-Z0-9_-]/g, function (ch) {
      return '\\' + ch;
    });
  }

  var EMAIL_CAPTURED_KEY = 'sidekick.recipeSave.email';
  var EMAIL_SNOOZE_KEY = 'sidekick.recipeSave.promptUntil';
  var NL_SUPPRESS_KEY = 'sidekick_newsletter_popup_suppressed_until';

  function hasCapturedEmail() {
    try {
      return Boolean(window.localStorage.getItem(EMAIL_CAPTURED_KEY));
    } catch (e) {
      return false;
    }
  }

  function isEmailPromptSnoozed() {
    try {
      var until = parseInt(window.localStorage.getItem(EMAIL_SNOOZE_KEY), 10);
      return Boolean(until && Date.now() < until);
    } catch (e) {
      return false;
    }
  }

  function snoozeEmailPrompt(days) {
    try {
      var until = Date.now() + days * 24 * 60 * 60 * 1000;
      window.localStorage.setItem(EMAIL_SNOOZE_KEY, String(until));
    } catch (e) { /* private mode */ }
  }

  function markEmailCaptured(email) {
    try {
      window.localStorage.setItem(EMAIL_CAPTURED_KEY, email || '1');
      snoozeEmailPrompt(365);
      var until = Date.now() + 30 * 24 * 60 * 60 * 1000;
      window.localStorage.setItem(NL_SUPPRESS_KEY, String(until));
    } catch (e) { /* private mode */ }
  }

  function maybeAskEmail(handle, title) {
    if (hasCapturedEmail() || isEmailPromptSnoozed()) return;
    var dialog = document.getElementById('recipe-save-email-dialog');
    if (!dialog || typeof dialog.showModal !== 'function') return;
    dialog.dataset.recipeHandle = handle || '';
    dialog.dataset.recipeTitle = title || '';
    if (!dialog.open) dialog.showModal();
    var input = dialog.querySelector('#rs-email-input');
    if (input) setTimeout(function () { input.focus(); }, 50);
  }

  function closeEmailDialog() {
    var dialog = document.getElementById('recipe-save-email-dialog');
    if (dialog && dialog.open) dialog.close();
  }

  function isValidEmail(value) {
    return typeof value === 'string' && /^.+@.+\..+$/.test(value.trim());
  }

  function subscribeRecipeSave(email, handle, title, done) {
    var dialog = document.getElementById('recipe-save-email-dialog');
    if (!dialog) {
      done(new Error('missing dialog'));
      return;
    }
    var key = dialog.dataset.klaviyoKey;
    var revision = dialog.dataset.klaviyoRevision;
    var listId = dialog.dataset.klaviyoListId;
    if (!key || !listId) {
      done(new Error('missing list'));
      return;
    }

    var form = dialog.querySelector('[data-rs-email-form]');
    var consentApi = window.SidekickMarketingConsent;
    var consentCheck = consentApi && consentApi.validate
      ? consentApi.validate(form, { emailConsentRequired: true })
      : { ok: true, error: '' };
    if (!consentCheck.ok) {
      done(new Error(consentCheck.error || 'Please tick the email box.'));
      return;
    }
    var consentValues = consentApi && consentApi.getValues ? consentApi.getValues(form) : {};
    var consentMetadata = consentApi && consentApi.getMetadata ? consentApi.getMetadata(form) : {};
    var profileAttributes = { email: email };
    var subscriptions = consentApi && consentApi.buildKlaviyoSubscriptions
      ? consentApi.buildKlaviyoSubscriptions(consentValues)
      : undefined;
    if (subscriptions) profileAttributes.subscriptions = subscriptions;
    var consentProps = consentApi && consentApi.buildKlaviyoConsentProperties
      ? consentApi.buildKlaviyoConsentProperties(consentValues, consentMetadata)
      : {};
    consentProps.Country = 'New Zealand';
    consentProps['Signup Market'] = 'NZ';
    consentProps['Signup Offer'] = 'WELCOME5';
    consentProps['Signup Source'] = 'Recipe save';
    if (handle) consentProps['Saved recipe'] = handle;
    if (title) consentProps['Saved recipe title'] = title;
    profileAttributes.properties = consentProps;

    var payload = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Sidekick recipe save',
          profile: {
            data: {
              type: 'profile',
              attributes: profileAttributes
            }
          }
        },
        relationships: {
          list: {
            data: { type: 'list', id: listId }
          }
        }
      }
    };

    fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(key), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        revision: revision || '2026-04-15'
      },
      body: JSON.stringify(payload)
    }).then(function (res) {
      if (!res.ok) throw new Error('Could not save email. Try again.');
      done(null);
    }).catch(function (err) {
      done(err);
    });
  }

  function wireEmailDialog() {
    var dialog = document.getElementById('recipe-save-email-dialog');
    if (!dialog) return;
    var form = dialog.querySelector('[data-rs-email-form]');
    var errorEl = dialog.querySelector('[data-rs-email-error]');
    var submitBtn = dialog.querySelector('[data-rs-email-submit]');

    dialog.querySelectorAll('[data-rs-email-skip]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        snoozeEmailPrompt(30);
        closeEmailDialog();
      });
    });

    dialog.addEventListener('cancel', function () {
      snoozeEmailPrompt(30);
    });

    if (!form) return;
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var emailInput = dialog.querySelector('#rs-email-input');
      var email = emailInput ? (emailInput.value || '').trim() : '';
      if (errorEl) {
        errorEl.hidden = true;
        errorEl.textContent = '';
      }
      if (!isValidEmail(email)) {
        if (errorEl) {
          errorEl.textContent = 'Please enter a valid email address.';
          errorEl.hidden = false;
        }
        if (emailInput) emailInput.focus();
        return;
      }
      if (submitBtn) submitBtn.disabled = true;
      subscribeRecipeSave(
        email,
        dialog.dataset.recipeHandle || '',
        dialog.dataset.recipeTitle || '',
        function (err) {
          if (submitBtn) submitBtn.disabled = false;
          if (err) {
            if (errorEl) {
              errorEl.textContent = err.message || 'Could not save email. Try again.';
              errorEl.hidden = false;
            }
            return;
          }
          markEmailCaptured(email);
          closeEmailDialog();
        }
      );
    });
  }

  function onClick(e) {
    var btn = e.target.closest('[data-recipe-save]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();

    var handle = btn.dataset.handle;
    if (!handle) return;
    var nowSaved = toggleSave(handle, btn.dataset.title || '');

    paintButton(btn);
    paintCard(handle);
    updateCounts();

    document.dispatchEvent(
      new CustomEvent('recipe-save:change', {
        detail: { handle: handle, saved: nowSaved }
      })
    );

    if (nowSaved) {
      btn.classList.remove('recipe-save--pop');
      void btn.offsetWidth;
      btn.classList.add('recipe-save--pop');
      maybeAskEmail(handle, btn.dataset.title || '');
    }
  }

  function init() {
    paintAllButtons();
    paintAllCards();
    updateCounts();
    wireEmailDialog();

    document.addEventListener('click', onClick);

    // Keep tabs in sync — a save in tab A reflects in tab B.
    window.addEventListener('storage', function (ev) {
      if (ev.key !== STORAGE_KEY) return;
      paintAllButtons();
      paintAllCards();
      updateCounts();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
